/**
 * @todo React Router 6+ does not support path-to-regexp routes. Regexs should
 * be removed in the future, and the paths reordered appropriately to match as
 * expected.
 */
export enum OpalRoutes {
  /** Moments Routes */
  Approvals = '/moments/:workspaceSlug/approvals',
  Assignments = '/moments/:workspaceSlug/assignments',
  Board = '/moments/:workspaceSlug/boards/:boardId',
  BoardBlock = `${Board}/blocks/:blockId/:blockViewMode?`,
  Boards = '/moments/:workspaceSlug/boards',
  /**
   * This route will conflict with WorkspaceDirectory below in React Router 5,
   * the underlying package path-to-regexp does not have support for a negative
   * lookahead to exclude "workspaces" (?!workspaces) on its pinned version.
   * Can fix when upgraded to React Router 6, but for now you may have to manually
   * work around if matching on these routes or using `useRouteDetails` below.
   */
  Calendar = '/moments/:workspaceSlug',
  ContentQueue = '/moments/:workspaceSlug/content_queue',
  Timelines = '/moments/:workspaceSlug/timelines',
  StoryCalendar = '/moments/:workspaceSlug/stories/:storyId(\\d+)', // use (\\d+) to match on ID only
  StoryTimelines = '/moments/:workspaceSlug/stories/:storyId(\\d+)/timelines',
  StoryView = '/moments/:workspaceSlug/stories',
  WorkspaceAssetLibrary = '/moments/:workspaceSlug/assets',
  WorkspaceBlock = '/moments/:workspaceSlug/blocks/:blockId/:blockViewMode?',
  WorkspaceDirectory = '/moments/workspaces',
  MomentExports = '/moments/:momentId(\\d+)/exports',

  /** Presentation Routes */
  Presentations = '/presentations',
  Presentation = '/presentations/:presentationId(\\d+)',
  PresentationSlide = '/presentations/:presentationId(\\d+)/slides/:slideId(\\d+)',
  PresentationShareGroups = '/presentations/:presentationId(\\d+)/presentation_share_groups/:presentationShareKey',
  PresentationExports = '/presentations/exports',
  PresentationInvalidLink = '/presentations/invalid_link',

  /** User Directory Routes */
  UserDirectory = '/people',
  User = '/people/:userId(\\d+)',

  /** Shrouds */
  MomentShroud = '/moments/:workspaceSlug/:momentId(\\d+|[0-9a-fA-F]{8}\\-[0-9a-fA-F]{4}\\-[0-9a-fA-F]{4}\\-[0-9a-fA-F]{4}\\-[0-9a-fA-F]{12})',
  ContentShroud = '/moments/:workspaceSlug/:momentId(\\d+)/posts/:postId(\\d+)',
  AssetZip = '/moments/:workspaceSlug/:momentId(\\d+)/posts/(\\d+)/assets/zip/:token',
  AssetShroud = '/moments/:workspaceSlug/:momentId(\\d+)/posts/:postId(\\d+)/assets/:assetId(\\d+)',
  StoryMomentShroud = '/moments/:workspaceSlug/stories/:storyId(\\d+)/moments/:momentId(\\d+)',
  StoryContentShroud = '/moments/:workspaceSlug/stories/:storyId(\\d+)/moments/:momentId(\\d+)/posts/:postId(\\d+)',
  StoryAssetShroud = '/moments/:workspaceSlug/stories/:storyId(\\d+)/moments/:momentId(\\d+)/posts/:postId(\\d+)/assets/:assetId(\\d+)',

  /** Plan View */
  Plans = '/moments/:workspaceSlug/plans',
  ChartPreview = `${Plans}/:planId/chart/:chartId`,
  Plan = `${Plans}/:planId/:planViewMode?`,
  Block = `${Plan}/blocks/:blockId/:blockViewMode?`,
  PlanBlockTemplates = `${Plan}/blockTemplates`,

  /** Workspace Admin */
  ConfigureWorkspace = '/moments/:workspaceSlug/configure/workspace',
  ConfigureLabelSets = '/moments/:workspaceSlug/configure/label_sets',
  ConfigureChannels = '/moments/:workspaceSlug/configure/accounts',
  ConfigureCustomFields = '/moments/:workspaceSlug/configure/custom_fields',
  ConfigureStamps = '/moments/:workspaceSlug/configure/stamps',
  ConfigureWorkflow = '/moments/:workspaceSlug/configure/approvers',
  ConfigureCategoryTypes = '/moments/:workspaceSlug/configure/category_types',
  ConfigureEveryoneSocial = '/moments/:workspaceSlug/configure/everyone_social',

  /** Opal Admin */
  ConfigureAllUsers = '/moments/workspaces/admin/users',
  ConfigureAllWorkspaces = '/moments/workspaces/admin/workspaces',
  ConfigureSharedLabelSets = '/moments/workspaces/admin/labels',
  ConfigureUserGroups = '/moments/workspaces/admin/user_groups',

  /** In-Market Preview */
  CalendarInMarketPreview = '/moments/:workspaceSlug/in_market_preview',

  /** Misc. */
  HelpCenter = '/helpcenter/sso/login',
}

/**
 * Routes that have :workspaceSlug as a segment
 */
export const workspaceScopedRoutes = [
  OpalRoutes.Approvals,
  OpalRoutes.AssetShroud,
  OpalRoutes.AssetZip,
  OpalRoutes.Assignments,
  OpalRoutes.Block,
  OpalRoutes.Board,
  OpalRoutes.BoardBlock,
  OpalRoutes.Boards,
  OpalRoutes.Calendar,
  OpalRoutes.CalendarInMarketPreview,
  OpalRoutes.ConfigureCategoryTypes,
  OpalRoutes.ConfigureChannels,
  OpalRoutes.ConfigureCustomFields,
  OpalRoutes.ConfigureEveryoneSocial,
  OpalRoutes.ConfigureLabelSets,
  OpalRoutes.ConfigureStamps,
  OpalRoutes.ConfigureWorkflow,
  OpalRoutes.ConfigureWorkspace,
  OpalRoutes.ContentQueue,
  OpalRoutes.ContentShroud,
  OpalRoutes.MomentShroud,
  OpalRoutes.Plan,
  OpalRoutes.PlanBlockTemplates,
  OpalRoutes.Plans,
  OpalRoutes.StoryAssetShroud,
  OpalRoutes.StoryCalendar,
  OpalRoutes.StoryContentShroud,
  OpalRoutes.StoryMomentShroud,
  OpalRoutes.StoryTimelines,
  OpalRoutes.StoryView,
  OpalRoutes.Timelines,
  OpalRoutes.WorkspaceAssetLibrary,
  OpalRoutes.WorkspaceBlock,
  OpalRoutes.ChartPreview,
]

export const navigationlessRoutes = [
  OpalRoutes.MomentShroud,
  OpalRoutes.ContentShroud,
  OpalRoutes.AssetShroud,
  OpalRoutes.StoryMomentShroud,
  OpalRoutes.StoryContentShroud,
  OpalRoutes.StoryAssetShroud,
  OpalRoutes.Presentation,
  OpalRoutes.PresentationShareGroups,
  OpalRoutes.PresentationSlide,
  OpalRoutes.PresentationExports,
  OpalRoutes.PresentationInvalidLink,
  OpalRoutes.MomentExports,
  OpalRoutes.ChartPreview,
]

export const shroudRoutes = [
  OpalRoutes.MomentShroud,
  OpalRoutes.StoryMomentShroud,
  OpalRoutes.ContentShroud,
  OpalRoutes.StoryContentShroud,
  OpalRoutes.WorkspaceAssetLibrary,
  OpalRoutes.AssetShroud,
  OpalRoutes.StoryAssetShroud,
]

export const unauthenticatedRoutes = [
  OpalRoutes.AssetZip,
  OpalRoutes.Presentation,
  OpalRoutes.PresentationShareGroups,
  OpalRoutes.PresentationSlide,
  OpalRoutes.PresentationInvalidLink,
]

export const asgardPersonalPanelRoutes = [
  OpalRoutes.Board,
  OpalRoutes.Boards,
  OpalRoutes.Block,
  OpalRoutes.Plan,
  OpalRoutes.Plans,
  OpalRoutes.WorkspaceBlock,
]

export const gemContextSupportedRoutes = [
  OpalRoutes.Block,
  OpalRoutes.Board,
  OpalRoutes.Boards,
  OpalRoutes.Calendar,
  OpalRoutes.StoryCalendar,
  OpalRoutes.ContentShroud,
  OpalRoutes.StoryContentShroud,
  OpalRoutes.MomentShroud,
  OpalRoutes.StoryMomentShroud,
  OpalRoutes.Plan,
  OpalRoutes.Plans,
]
